import {
  PButton,
  PButtonPure,
  PSelect,
  PSelectOption,
  PSelectProps,
} from '@porsche-design-system/components-react';
import { Column, RowData } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { ActionGroup, Popover } from '../..';
import type { FilterTableFeatures } from '../table-features';

export const SelectFilter = <
  TFeatures extends FilterTableFeatures,
  T extends RowData,
>({
  column,
  options,
  i18n,
}: {
  column: Column<TFeatures, T, unknown>;
  options: { label: string; value: string | number }[];
  i18n: {
    filterLabel: string;
    optionAll: string;
    buttonReset: string;
    buttonFilter: string;
  };
}) => {
  const [isVisible, setVisibility] = useState(false);
  const filterColumn = column as unknown as Column<
    FilterTableFeatures,
    T,
    unknown
  >;
  const filterValue = filterColumn.getFilterValue() as string;
  const [selectValue, setSelectValue] = useState(filterValue);

  useEffect(() => {
    setSelectValue(filterValue);
  }, [filterValue]);

  return (
    <Popover
      target={
        <PButtonPure
          type="button"
          active={!!filterValue}
          size="xs"
          icon="filter"
          onClick={() => setVisibility((value) => !value)}
          hideLabel
        >
          {i18n.filterLabel}
        </PButtonPure>
      }
      open={isVisible}
      onChange={setVisibility}
      placement="right-start"
      withPortal
    >
      <>
        <PSelect
          label={i18n.filterLabel}
          aria-label={i18n.filterLabel}
          hideLabel
          filter
          name={i18n.filterLabel}
          value={selectValue || undefined}
          onChange={(e) => {
            const { value } = e.target as HTMLElement & PSelectProps;
            setSelectValue(value);
          }}
        >
          <PSelectOption value="">{i18n.optionAll}</PSelectOption>
          {options.map(({ label, value }) => (
            <PSelectOption key={label} value={String(value)}>
              {label}
            </PSelectOption>
          ))}
        </PSelect>

        <ActionGroup spacing="md">
          <PButton
            type="button"
            variant="secondary"
            icon="close"
            onClick={() => {
              filterColumn.setFilterValue(undefined);
              setVisibility(false);
            }}
          >
            {i18n.buttonReset}
          </PButton>
          <PButton
            type="button"
            onClick={() => {
              filterColumn.setFilterValue(selectValue || undefined);
              setVisibility(false);
            }}
          >
            {i18n.buttonFilter}
          </PButton>
        </ActionGroup>
      </>
    </Popover>
  );
};
